import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    AlertTriangle, CalendarDays, Check, Clock3, CreditCard, Eye, EyeOff,
    Download, FileText, Gift, IndianRupee, Pencil, Phone, Plus, RefreshCw, Search, ShieldCheck,
    Sparkles, Store, Trash2, UserRound, Users, X,
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import BrandLoader from '../../components/BrandLoader';
import Navbar from '../../components/Navbar';
import ConfirmDialog from '../../components/ConfirmDialog';
import Select from '../../components/Select';
import Pagination, { paginate } from '../../components/Pagination';
import API from '../../utils/api';
import SubscriptionInvoice from '../../components/Invoice/SubscriptionInvoice';
import { downloadSubscriptionInvoicePdf } from '../../utils/subscriptionInvoicePdf';
import toast from 'react-hot-toast';
import './Plans.css';

const makeEmptyForm = targetType => ({
    name: '', targetType, price: '', gstPercentage: '0', originalPrice: '', durationValue: '12', durationUnit: 'months', noExpiry: false, features: [''],
});

const PLAN_TEMPLATES = {
    free: { name: 'Free', price: '0', gstPercentage: '0', originalPrice: '', durationValue: '1', durationUnit: 'years', features: ['Create profile', 'Browse verified profiles', 'Send limited interests'] },
    basic: { name: 'Basic', price: '999', gstPercentage: '18', originalPrice: '1499', durationValue: '3', durationUnit: 'months', features: ['View contact details', 'Send unlimited interests', 'Direct messaging', 'Basic support'] },
    premium: { name: 'Premium', price: '2200', gstPercentage: '18', originalPrice: '5000', durationValue: '1', durationUnit: 'years', features: ['Unlimited contact views', 'Unlimited interests and messages', 'Horoscope compatibility', 'Priority profile listing', 'WhatsApp support'] },
};

const pricingForPlan = plan => {
    const subtotal = Number(plan?.price || 0);
    const gstPercentage = Number(plan?.gstPercentage || 0);
    const gstAmount = Math.round((subtotal * gstPercentage / 100 + Number.EPSILON) * 100) / 100;
    return { subtotal, gstPercentage, gstAmount, total: Math.round((subtotal + gstAmount + Number.EPSILON) * 100) / 100 };
};

const validityText = (value, unit = 'months') => {
    const amount = Number(value) || 0;
    return `${amount} ${amount === 1 ? unit.replace(/s$/, '') : unit}`;
};

// what a plan / subscription shows as its validity
const planValidity = item => (item?.noExpiry ? 'Lifetime' : validityText(item?.durationValue || item?.durationMonths || 12, item?.durationUnit || 'months'));

const detailDate = value => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const detailMoney = (amount, currency = 'INR') => new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: currency || 'INR', maximumFractionDigits: 2,
}).format(Number(amount || 0));

const titleCase = value => String(value || '').replace(/[_-]/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());

const Plans = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [plans, setPlans] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [invoiceSubscription, setInvoiceSubscription] = useState(null);
    const [users, setUsers] = useState([]);
    const [typeFilter, setTypeFilter] = useState('member');
    const [showForm, setShowForm] = useState(false);
    const [editingPlanId, setEditingPlanId] = useState(null);
    const [form, setForm] = useState(makeEmptyForm('member'));
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [assignUserId, setAssignUserId] = useState('');
    const [assignPlanId, setAssignPlanId] = useState('');
    const [assigning, setAssigning] = useState(false);
    const [assignMode, setAssignMode] = useState('gift');
    const [assignNote, setAssignNote] = useState('');
    const [pendingDelete, setPendingDelete] = useState(null);
    const [historySearch, setHistorySearch] = useState({ member: '', service: '' });
    const [historyTab, setHistoryTab] = useState('member');   // which audience's history is on show
    const [historyPage, setHistoryPage] = useState(1);
    const HISTORY_PAGE_SIZE = 10;
    const [applyExisting, setApplyExisting] = useState(false);   // push a changed validity onto people who already hold the plan
    const [overrides, setOverrides] = useState({});              // subscriptionId -> 'keep' | 'lifetime'
    const [detailSub, setDetailSub] = useState(null);            // subscription detail dialog (or { manual, user } when premium was set by hand)
    const [moving, setMoving] = useState(false);

    const fetchAll = useCallback(async (silent = false) => {
        if (!silent) setRefreshing(true);
        try {
            const [plansResponse, subscriptionsResponse, usersResponse, vendorsResponse] = await Promise.all([
                API.get('/plans/admin/all'),
                API.get('/subscriptions/admin/all'),
                API.get('/admin/users'),
                API.get('/admin/vendors'),
            ]);
            setPlans(plansResponse.data.plans || []);
            setSubscriptions(subscriptionsResponse.data.subscriptions || []);
            setUsers([...(usersResponse.data.users || []), ...(vendorsResponse.data.vendors || [])]);
        } catch (error) {
            toast.error('Failed to load plans');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => { fetchAll(true); }, [fetchAll]);

    useEffect(() => {
        if (!detailSub) return undefined;
        const previousOverflow = document.body.style.overflow;
        const closeOnEscape = event => { if (event.key === 'Escape') setDetailSub(null); };
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', closeOnEscape);
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', closeOnEscape);
        };
    }, [detailSub]);

    const openCreate = () => {
        setEditingPlanId(null);
        setForm(makeEmptyForm(typeFilter));
        setShowForm(true);
    };

    const openEdit = plan => {
        setEditingPlanId(plan._id);
        setForm({
            name: plan.name,
            targetType: plan.targetType,
            price: String(plan.price),
            gstPercentage: String(plan.gstPercentage || 0),
            originalPrice: plan.originalPrice ? String(plan.originalPrice) : '',
            durationValue: String(plan.durationValue || plan.durationMonths || 12),
            durationUnit: plan.durationUnit || 'months',
            noExpiry: !!plan.noExpiry,
            features: plan.features?.length ? [...plan.features] : [''],
        });
        setShowForm(true);
        window.requestAnimationFrame(() => document.getElementById('plan-editor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    };

    const closeForm = () => {
        setApplyExisting(false);
        setOverrides({});
        setShowForm(false);
        setEditingPlanId(null);
        setForm(makeEmptyForm(typeFilter));
    };

    const editingPlan = plans.find(plan => plan._id === editingPlanId);
    const activePlanSubs = useMemo(() => (editingPlanId
        ? subscriptions.filter(sub => String(sub.plan) === String(editingPlanId) && !sub.cancelledAt && new Date(sub.expiresAt) > new Date())
        : []), [subscriptions, editingPlanId]);
    const validityChanged = !!editingPlan && (!!editingPlan.noExpiry !== !!form.noExpiry || (!form.noExpiry && (
        Number(form.durationValue) !== Number(editingPlan.durationValue || editingPlan.durationMonths || 12) || form.durationUnit !== (editingPlan.durationUnit || 'months'))));
    useEffect(() => { setApplyExisting(validityChanged); }, [validityChanged]);

    const handleSave = async event => {
        event.preventDefault();
        const features = form.features.map(feature => feature.trim()).filter(Boolean);
        if (!features.length) return toast.error('Add at least one plan feature');
        setSaving(true);
        const payload = {
            name: form.name.trim(),
            targetType: form.targetType,
            price: Number(form.price),
            gstPercentage: Number(form.gstPercentage || 0),
            originalPrice: form.originalPrice ? Number(form.originalPrice) : null,
            noExpiry: !!form.noExpiry,
            durationValue: Number(form.durationValue) || 1,
            durationUnit: form.durationUnit,
            features,
        };
        try {
            if (editingPlanId) {
                await API.put(`/plans/${editingPlanId}`, payload);
                if (applyExisting && activePlanSubs.length) {
                    const { data } = await API.post(`/plans/${editingPlanId}/apply-validity`, { overrides });
                    toast.success(data.message || 'Plan and existing subscriptions updated');
                } else {
                    toast.success('Plan updated for future subscriptions');
                }
            } else {
                await API.post('/plans', payload);
                toast.success('Plan created');
            }
            closeForm();
            fetchAll(true);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save plan');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async plan => {
        try {
            await API.put(`/plans/${plan._id}`, { isActive: !plan.isActive });
            toast.success(plan.isActive ? 'Plan hidden from new signups' : 'Plan is live again');
            fetchAll(true);
        } catch (error) {
            toast.error('Failed to update plan');
        }
    };

    const confirmDeletePlan = async () => {
        const plan = pendingDelete;
        setPendingDelete(null);
        try {
            await API.delete(`/plans/${plan.id}`);
            toast.success('Plan deleted');
            fetchAll(true);
        } catch (error) {
            toast.error('Failed to delete plan');
        }
    };

    const loadRazorpay = () => new Promise(resolve => {
        if (window.Razorpay) return resolve(true);
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });

    // Paid: Razorpay Checkout opens here in the admin panel; the plan is only activated after the
    // server has verified the payment with Razorpay.
    const handlePaidAssign = async () => {
        const account = audienceUsers.find(user => user._id === assignUserId);
        setAssigning(true);
        try {
            if (!(await loadRazorpay())) throw new Error('Could not load Razorpay. Check your connection and try again.');
            const { data: order } = await API.post('/payments/create-order', { planId: assignPlanId, userId: assignUserId });
            const razorpay = new window.Razorpay({
                key: order.keyId,
                amount: order.amount,
                currency: order.currency,
                order_id: order.orderId,
                name: 'Gettimelam Matrimony',
                description: `${order.plan.name} for ${account?.businessName || account?.name || 'member'}${order.pricing?.gstPercentage ? ` · Includes ${order.pricing.gstPercentage}% GST` : ''}`,
                image: `${window.location.origin}/logo.png`, // Razorpay renders this on its own domain, so it needs an absolute URL
                prefill: { name: account?.name, email: account?.email, contact: account?.mobile },
                theme: { color: '#7A1733' },
                // UPI and cards only: no netbanking or wallets
                config: { display: { blocks: { upi: { name: 'Pay using UPI', instruments: [{ method: 'upi' }] }, card: { name: 'Pay using Card', instruments: [{ method: 'card' }] } }, sequence: ['block.upi', 'block.card'], preferences: { show_default_blocks: false } } },
                handler: async response => {
                    try {
                        const { data } = await API.post('/payments/verify', {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                            planId: assignPlanId,
                            userId: assignUserId,
                            note: assignNote,
                        });
                        toast.success(data.message || 'Payment received and plan activated');
                        setAssignUserId(''); setAssignPlanId(''); setAssignNote('');
                        fetchAll(true);
                    } catch (error) {
                        toast.error(error.response?.data?.message || 'Payment verification failed — check Razorpay before retrying');
                    } finally {
                        setAssigning(false);
                    }
                },
                modal: { ondismiss: () => setAssigning(false) },
            });
            razorpay.on('payment.failed', response => {
                toast.error('Payment failed: ' + (response.error?.description || 'please try again'));
                setAssigning(false);
            });
            razorpay.open();
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || 'Could not start the payment');
            setAssigning(false);
        }
    };

    const handleAssign = async event => {
        event.preventDefault();
        if (!assignUserId || !assignPlanId) return toast.error('Pick a user and a plan');
        if (assignMode === 'paid') return handlePaidAssign();
        setAssigning(true);
        try {
            const response = await API.post('/subscriptions/assign', {
                userId: assignUserId,
                planId: assignPlanId,
                mode: 'gift',
                note: assignNote,
            });
            toast.success(response.data.message);
            setAssignUserId('');
            setAssignPlanId('');
            setAssignNote('');
            fetchAll(true);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to assign plan');
        } finally {
            setAssigning(false);
        }
    };

    const visiblePlans = useMemo(() => plans.filter(plan => plan.targetType === typeFilter), [plans, typeFilter]);
    const selectedAssignPlan = plans.find(plan => plan._id === assignPlanId);
    const assignablePlans = useMemo(() => visiblePlans.filter(plan => plan.isActive), [visiblePlans]);
    const typeSubscriptions = useMemo(() => subscriptions.filter(subscription => subscription.targetType === typeFilter), [subscriptions, typeFilter]);
    const activeSubscriptions = useMemo(() => typeSubscriptions.filter(subscription => new Date(subscription.expiresAt) > new Date()), [typeSubscriptions]);
    const totalRevenue = activeSubscriptions.reduce((sum, subscription) => sum + (subscription.totalPaidSnapshot ?? subscription.priceSnapshot ?? 0), 0);
    const activePlanCount = visiblePlans.filter(plan => plan.isActive).length;
    const audienceUsers = users.filter(user => user.role === typeFilter);
    const popularPlanId = visiblePlans.reduce((best, plan) => {
        const count = typeSubscriptions.filter(subscription => String(subscription.plan) === String(plan._id)).length;
        return count > best.count ? { id: plan._id, count } : best;
    }, { id: null, count: 0 }).id;


    const statusFor = expiresAt => {
        const days = Math.ceil((new Date(expiresAt) - new Date()) / 86400000);
        return days > 0
            ? { active: true, text: `${days} day${days === 1 ? '' : 's'} left` }
            : { active: false, text: `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago` };
    };

    // arriving from Users > "Upgrade to premium": preselect that member and the Premium plan, then bring the form into view
    useEffect(() => {
        const userId = searchParams.get('user');
        if (!userId || loading) return;
        const account = users.find(user => user._id === userId);
        if (searchParams.get('view')) {
            // arriving from Users > "Move to free plan": open that member's running plan
            const current = subscriptions
                .filter(sub => String(sub.user?._id || sub.user) === userId && !sub.cancelledAt && new Date(sub.expiresAt) > new Date())
                .sort((a, b) => new Date(b.purchasedAt) - new Date(a.purchasedAt))[0];
            if (current) setDetailSub(current);
            else if (account) setDetailSub({ manual: true, user: account });
            else toast.error('That account could not be found');
            setSearchParams({}, { replace: true });
            return;
        }
        if (account) {
            const type = account.role === 'service' ? 'service' : 'member';
            setTypeFilter(type);
            setAssignUserId(userId);
            const premium = plans.find(plan => plan.targetType === type && plan.isActive && /premium/i.test(plan.name));
            setAssignPlanId(premium?._id || '');
            if (!premium) toast.error('No published Premium plan found — pick a plan');
            setTimeout(() => document.querySelector('.plans-assign')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
        } else {
            toast.error('That account could not be found');
        }
        setSearchParams({}, { replace: true });
    }, [searchParams, loading, users, plans, subscriptions, setSearchParams]);

    const detailStatus = sub => (sub.cancelledAt ? 'Cancelled' : sub.noExpiry ? 'Active · lifetime' : new Date(sub.expiresAt) > new Date() ? 'Active' : 'Expired');
    const detailIsLive = sub => !!sub && !sub.cancelledAt && (sub.noExpiry || new Date(sub.expiresAt) > new Date());

    const detailUser = detailSub?.user || {};
    const detailCustomer = detailSub?.customerSnapshot || {};
    const detailTotal = detailSub?.totalPaidSnapshot ?? detailSub?.priceSnapshot ?? 0;
    const detailSubtotal = detailSub?.subtotalSnapshot ?? detailTotal;
    const detailTax = detailSub?.taxSnapshot ?? 0;
    const detailAddress = detailSub ? [
        detailCustomer.addressLine1, detailCustomer.addressLine2, detailCustomer.city,
        detailCustomer.district, detailCustomer.state, detailCustomer.country,
    ].filter(Boolean).join(', ') : '';
    const detailSource = detailSub?.paymentStatus === 'gifted'
        ? 'Gifted by admin'
        : detailSub?.assignedBy === 'self'
            ? 'Self-purchased online'
            : detailSub?.paymentStatus === 'paid'
                ? 'Payment recorded by admin'
                : 'Assigned by admin';

    const handleMoveToFree = async () => {
        if (!detailSub) return;
        setMoving(true);
        try {
            if (detailSub.manual) await API.put(`/admin/users/${detailSub.user._id}/toggle-premium`);
            else await API.post(`/subscriptions/admin/${detailSub._id}/cancel`);
            toast.success('Moved to the Free plan');
            setDetailSub(null);
            fetchAll(true);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Could not move to the Free plan');
        } finally {
            setMoving(false);
        }
    };

    const changeAudience = type => {
        setTypeFilter(type);
        setAssignUserId('');
        setAssignPlanId('');
        setHistorySearch({ member: '', service: '' });
        if (showForm) {
            setEditingPlanId(null);
            setForm(makeEmptyForm(type));
        }
    };

    // one subscription history table, switched between members and service providers with tabs (10 rows a page)
    const renderHistory = kind => {
        const search = historySearch[kind];
        const query = search.trim().toLowerCase();
        const rows = subscriptions.filter(subscription => subscription.targetType === kind && (!query || [
            subscription.user?.name, subscription.user?.businessName, subscription.user?.mobile, subscription.planNameSnapshot,
        ].some(value => String(value || '').toLowerCase().includes(query))));
        const pageRows = paginate(rows, historyPage, HISTORY_PAGE_SIZE);
        const countFor = type => subscriptions.filter(subscription => subscription.targetType === type).length;
        return (
                    <section className="plans-subscribers" key={kind}>
                        <div className="plans-subscribers__header">
                            <div><h2>Subscription history</h2><p>Latest {kind === 'member' ? 'member' : 'service provider'} plan assignments</p></div>
                            <label><Search size={15} /><input placeholder="Search subscriber or plan…" value={search} onChange={event => { setHistorySearch(prev => ({ ...prev, [kind]: event.target.value })); setHistoryPage(1); }} />{search && <button onClick={() => { setHistorySearch(prev => ({ ...prev, [kind]: '' })); setHistoryPage(1); }}><X size={13} /></button>}</label>
                        </div>
                        <div className="plans-subscribers__tabs plans-audience-tabs" role="tablist" aria-label="Subscription history audience">
                            {[{ id: 'member', label: 'Members' }, { id: 'service', label: 'Service providers' }].map(tab => (
                                <button key={tab.id} type="button" role="tab" aria-selected={kind === tab.id} className={kind === tab.id ? 'is-active' : ''} onClick={() => { setHistoryTab(tab.id); setHistoryPage(1); }}>
                                    {tab.label}<b>{countFor(tab.id)}</b>
                                </button>
                            ))}
                        </div>
                        <div className="plans-table-wrap">
                            <table>
                                <thead><tr><th>Subscriber</th><th>Plan</th><th>Validity</th><th>Price</th><th>Assigned</th><th>Expires</th><th>Status</th><th>Invoice</th></tr></thead>
                                <tbody>
                                    {pageRows.map(subscription => {
                                        const status = subscription.cancelledAt ? { active: false, text: 'Cancelled' } : subscription.noExpiry ? { active: true, text: 'Lifetime' } : statusFor(subscription.expiresAt);
                                        return <tr key={subscription._id}>
                                            <td><div className="plans-person"><span><UserRound size={15} /></span><div><b>{subscription.user?.name || subscription.user?.businessName || 'Unknown account'}</b><small><Phone size={11} />{subscription.user?.mobile || 'No mobile'}</small></div></div></td>
                                            <td><b className="plans-plan-name">{subscription.planNameSnapshot}</b></td>
                                            <td><span className="plans-validity"><CalendarDays size={13} />{planValidity(subscription)}</span></td>
                                            <td><b>{subscription.paymentStatus === 'gifted' ? 'Free' : `₹${Number((subscription.totalPaidSnapshot ?? subscription.priceSnapshot) || 0).toLocaleString('en-IN')}`}</b><small className={`plans-source plans-source--${subscription.paymentStatus || 'assigned'}`}>{subscription.paymentStatus === 'gifted' ? 'Gifted by admin' : subscription.paymentStatus === 'paid' && subscription.assignedBy === 'admin' ? `Paid · ${subscription.paymentMethod === 'razorpay' ? 'Razorpay (by admin)' : subscription.paymentMethod || 'offline'}` : subscription.assignedBy === 'self' ? 'Online' : 'Assigned'}</small></td>
                                            <td>{new Date(subscription.purchasedAt).toLocaleDateString('en-IN')}</td>
                                            <td>{subscription.noExpiry ? 'No expiry' : new Date(subscription.expiresAt).toLocaleDateString('en-IN')}</td>
                                            <td><span className={`plans-status ${status.active ? 'is-active' : 'is-expired'}`}>{status.active ? <Check size={12} /> : <X size={12} />}{status.text}</span></td>
                                            <td>
                                                <div className="plans-invoice-actions">
                                                    <button type="button" title="Plan details" onClick={() => setDetailSub(subscription)}><Eye size={14} /> Details</button>
                                                    <button type="button" title="View invoice" onClick={() => setInvoiceSubscription(subscription)}><FileText size={14} /> View</button>
                                                    <button type="button" title="Download invoice PDF" onClick={() => downloadSubscriptionInvoicePdf(subscription, subscription.user || {})}><Download size={14} /> PDF</button>
                                                </div>
                                            </td>
                                        </tr>;
                                    })}
                                    {!rows.length && <tr><td colSpan={8}><div className="plans-table-empty"><Users size={21} />{search ? 'No matching subscriptions' : 'No subscriptions for this audience yet'}</div></td></tr>}
                                </tbody>
                            </table>
                        </div>
                        <Pagination page={historyPage} pageSize={HISTORY_PAGE_SIZE} total={rows.length} onChange={setHistoryPage} />
                    </section>
        );
    };

    return (
        <div className="plans-layout">
            <Sidebar />
            <main className="admin-content plans-content">
                <Navbar />
                <div className="plans-page">
                    <header className="plans-heading">
                        <div>
                            <div className="plans-eyebrow"><Sparkles size={14} /> Revenue workspace</div>
                            <h1>Plans & subscriptions</h1>
                            <p>Create pricing plans, manage visibility and assign subscriptions.</p>
                        </div>
                        <div className="plans-heading__actions">
                            <button className="plans-refresh" onClick={() => fetchAll(false)} disabled={refreshing}><RefreshCw size={16} className={refreshing ? 'is-spinning' : ''} /> Refresh</button>
                            <button className="plans-create" onClick={showForm ? closeForm : openCreate}>{showForm ? <X size={16} /> : <Plus size={16} />}{showForm ? 'Close editor' : 'Create plan'}</button>
                        </div>
                    </header>

                    <section className="plans-audience-bar">
                        <div className="plans-audience-tabs">
                            <button className={typeFilter === 'member' ? 'is-active' : ''} onClick={() => changeAudience('member')}><Users size={16} /> Member plans <b>{plans.filter(plan => plan.targetType === 'member').length}</b></button>
                            <button className={typeFilter === 'service' ? 'is-active' : ''} onClick={() => changeAudience('service')}><Store size={16} /> Provider plans <b>{plans.filter(plan => plan.targetType === 'service').length}</b></button>
                        </div>
                        <span><ShieldCheck size={15} /> Existing subscriptions keep their original benefits and price.</span>
                    </section>

                    <section className="plans-stats">
                        <article><span className="is-primary"><IndianRupee size={19} /></span><div><strong>₹{totalRevenue.toLocaleString('en-IN')}</strong><p>Active subscription value</p></div></article>
                        <article><span className="is-green"><Users size={19} /></span><div><strong>{activeSubscriptions.length}</strong><p>Active subscribers</p></div></article>
                        <article><span className="is-amber"><Clock3 size={19} /></span><div><strong>{typeSubscriptions.length - activeSubscriptions.length}</strong><p>Expired subscriptions</p></div></article>
                        <article><span className="is-violet"><CreditCard size={19} /></span><div><strong>{activePlanCount}</strong><p>Published plans</p></div></article>
                    </section>

                    {showForm && (
                        <form id="plan-editor" className="plans-editor" onSubmit={handleSave}>
                            <div className="plans-editor__header">
                                <div className="plans-editor__icon">{editingPlanId ? <Pencil size={19} /> : <Plus size={19} />}</div>
                                <div><h2>{editingPlanId ? 'Edit plan' : 'Create a new plan'}</h2><p>{editingPlanId ? 'Updates apply only to future subscriptions.' : `Build a plan for ${typeFilter === 'member' ? 'matrimony members' : 'service providers'}.`}</p></div>
                                <button type="button" onClick={closeForm}><X size={17} /></button>
                            </div>

                            {!editingPlanId && (
                                <div className="plans-templates"><span>QUICK START</span>{Object.entries(PLAN_TEMPLATES).map(([key, template]) => <button type="button" key={key} onClick={() => setForm(current => ({ ...current, ...template }))}>{key === 'free' ? <Users size={14} /> : key === 'basic' ? <CreditCard size={14} /> : <Gift size={14} />}{template.name}</button>)}</div>
                            )}

                            <div className="plans-editor__body">
                                <div className="plans-editor__fields">
                                    <label><span>Plan name</span><input placeholder="e.g. Gold Membership" value={form.name} onChange={event => setForm({ ...form, name: event.target.value })} required /></label>
                                    <label><span>Audience</span><Select value={form.targetType} onChange={value => setForm({ ...form, targetType: value })} disabled={Boolean(editingPlanId)} allowEmpty={false} ariaLabel="Audience" options={[{ value: 'member', label: 'Matrimony members' }, { value: 'service', label: 'Service providers' }]} /></label>
                                    <label><span>Offer price (₹)</span><input type="number" min="0" placeholder="999" value={form.price} onChange={event => setForm({ ...form, price: event.target.value })} required /></label>
                                    <label><span>Original price (₹)</span><input type="number" min="0" placeholder="Optional" value={form.originalPrice} onChange={event => setForm({ ...form, originalPrice: event.target.value })} /></label>
                                    <label className="plans-gst-field"><span>GST percentage</span><div><input type="number" min="0" max="100" step="0.01" placeholder="18" value={form.gstPercentage} onChange={event => setForm({ ...form, gstPercentage: event.target.value })} /><b>%</b></div><small>Customer pays ₹{pricingForPlan(form).total.toLocaleString('en-IN')} including ₹{pricingForPlan(form).gstAmount.toLocaleString('en-IN')} GST</small></label>
                                    <label className="plans-duration"><span>Validity</span><div className="plans-noexpiry"><input type="checkbox" checked={!!form.noExpiry} onChange={event => setForm({ ...form, noExpiry: event.target.checked })} /><b>No expiration</b><em>Plan stays active for life</em></div><div style={form.noExpiry ? { opacity: .45, pointerEvents: 'none' } : undefined}><input type="number" min="1" max={form.durationUnit === 'days' ? 3650 : form.durationUnit === 'months' ? 120 : 10} value={form.durationValue} onChange={event => setForm({ ...form, durationValue: event.target.value })} required={!form.noExpiry} disabled={!!form.noExpiry} /><Select value={form.durationUnit} onChange={value => setForm({ ...form, durationUnit: value })} allowEmpty={false} ariaLabel="Validity unit" options={[{ value: 'days', label: 'Days' }, { value: 'months', label: 'Months' }, { value: 'years', label: 'Years' }]} /></div><small>{form.noExpiry ? 'Unlimited — never expires' : validityText(form.durationValue, form.durationUnit)}</small></label>
                                </div>

                                <div className="plans-features">
                                    <div className="plans-features__header"><div><h3>Plan benefits</h3><p>Add each benefit as a separate item.</p></div><span>{form.features.filter(feature => feature.trim()).length} added</span></div>
                                    <div className="plans-features__list">
                                        {form.features.map((feature, index) => (
                                            <div key={index}><span>{index + 1}</span><input value={feature} placeholder={index ? 'Add another benefit' : 'e.g. Unlimited contact views'} onChange={event => { const features = [...form.features]; features[index] = event.target.value; setForm({ ...form, features }); }} /><button type="button" disabled={form.features.length === 1} onClick={() => setForm({ ...form, features: form.features.filter((_, itemIndex) => itemIndex !== index) })}><X size={14} /></button></div>
                                        ))}
                                    </div>
                                    <button className="plans-add-feature" type="button" onClick={() => setForm({ ...form, features: [...form.features, ''] })}><Plus size={14} /> Add benefit</button>
                                </div>
                            </div>

                            {editingPlanId && activePlanSubs.length > 0 && (
                                <div className="plans-existing">
                                    <label className="plans-existing__toggle">
                                        <input type="checkbox" checked={applyExisting} onChange={event => setApplyExisting(event.target.checked)} />
                                        <span><b>Apply this validity to existing subscribers ({activePlanSubs.length})</b><em>{form.noExpiry ? 'They all become lifetime unless you keep some below.' : `Their end date becomes start date + ${validityText(form.durationValue, form.durationUnit)}.`}</em></span>
                                    </label>
                                    {applyExisting && (
                                        <div className="plans-existing__list">
                                            {activePlanSubs.map(sub => (
                                                <div key={sub._id} className="plans-existing__row">
                                                    <div><b>{sub.user?.name || sub.user?.businessName || 'Unknown'}</b><small>{sub.user?.mobile} · {sub.noExpiry ? 'lifetime now' : `ends ${new Date(sub.expiresAt).toLocaleDateString('en-IN')}`}</small></div>
                                                    <Select value={overrides[sub._id] || 'apply'} allowEmpty={false} ariaLabel="What to do for this subscriber"
                                                        onChange={value => setOverrides(prev => { const next = { ...prev }; if (value === 'apply') delete next[sub._id]; else next[sub._id] = value; return next; })}
                                                        options={[{ value: 'apply', label: form.noExpiry ? 'Make lifetime' : 'Apply new validity' }, { value: 'keep', label: 'Keep current period' }, ...(form.noExpiry ? [] : [{ value: 'lifetime', label: 'Make lifetime' }])]} />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="plans-editor__footer"><p><Clock3 size={14} /> Existing subscribers are never changed.</p><div><button type="button" onClick={closeForm}>Cancel</button><button type="submit" disabled={saving}><CreditCard size={15} />{saving ? 'Saving…' : editingPlanId ? 'Save changes' : 'Create plan'}</button></div></div>
                        </form>
                    )}

                    <section className="plans-catalogue">
                        <div className="plans-section-heading"><div><h2>{typeFilter === 'member' ? 'Member pricing' : 'Provider pricing'}</h2><p>{visiblePlans.length} plan{visiblePlans.length === 1 ? '' : 's'} configured for this audience</p></div><button onClick={openCreate}><Plus size={15} /> Add plan</button></div>
                        {loading ? (
                            <div className="plans-empty"><BrandLoader size={44} /><b>Loading plans</b></div>
                        ) : visiblePlans.length === 0 ? (
                            <div className="plans-empty"><CreditCard size={26} /><b>No plans yet</b><p>Create the first plan for this audience.</p><button onClick={openCreate}><Plus size={15} /> Create plan</button></div>
                        ) : (
                            <div className="plans-grid">
                                {visiblePlans.map((plan, index) => {
                                    // Free is the default state of an account and has no subscription record, so its count is every account not on a paid plan
                                    const isFreePlan = Number(plan.price) === 0;
                                    const subscriptionCount = isFreePlan ? audienceUsers.filter(account => !account.isPremium).length : typeSubscriptions.filter(subscription => String(subscription.plan) === String(plan._id) || subscription.planNameSnapshot === plan.name).length;
                                    const discount = plan.originalPrice > plan.price ? Math.round((1 - plan.price / plan.originalPrice) * 100) : 0;
                                    return (
                                        <article className={`plan-card plan-card--tone-${index % 5} ${!plan.isActive ? 'is-hidden' : ''} ${popularPlanId === plan._id ? 'is-featured' : ''}`} key={plan._id}>
                                            {discount > 0 && Number(plan.price) > 0 && <span className="plan-card__save">Save {discount}%</span>}
                                            <div className="plan-card__top">
                                                <div className="plan-card__badges">
                                                    <span className={plan.isActive ? 'is-live' : 'is-draft'}>{plan.isActive ? <Eye size={12} /> : <EyeOff size={12} />}{plan.isActive ? 'Published' : 'Hidden'}</span>
                                                    {popularPlanId === plan._id && <span className="is-popular"><Sparkles size={12} /> Most popular</span>}
                                                </div>
                                                <div className="plan-card__menu"><button onClick={() => openEdit(plan)} title="Edit plan"><Pencil size={15} /></button><button onClick={() => setPendingDelete({ id: plan._id, name: plan.name })} title="Delete plan"><Trash2 size={15} /></button></div>
                                            </div>
                                            <div className="plan-card__name"><div><CreditCard size={19} /></div><span><h3>{plan.name}</h3><p>{plan.targetType === 'service' ? 'Service provider plan' : 'Matrimony member plan'}</p></span></div>
                                            {Number(plan.price) === 0 ? (
                                                // the Free plan has no amount to show: no rupee price, no GST line, no total
                                                <div className="plan-card__price"><strong>Free</strong><small>/ {planValidity(plan)}</small></div>
                                            ) : (
                                                <>
                                                    {discount > 0 && <del className="plan-card__was">₹{Number(plan.originalPrice).toLocaleString('en-IN')}</del>}
                                                    <div className="plan-card__price"><span>₹</span><strong>{Number(plan.price).toLocaleString('en-IN')}</strong><small>/ {planValidity(plan)}</small></div>
                                                    <div className="plan-card__gst">+ {Number(plan.gstPercentage || 0)}% GST <b>Total ₹{pricingForPlan(plan).total.toLocaleString('en-IN')}</b></div>
                                                </>
                                            )}
                                            <div className="plan-card__validity"><CalendarDays size={13} />{plan.noExpiry ? 'No expiration · lifetime access' : `Valid for ${planValidity(plan)}`}</div>
                                            <div className="plan-card__features">
                                                {(plan.features || []).map(feature => <div key={feature}><Check size={14} /> <span>{feature}</span></div>)}
                                                {!plan.features?.length && <p>No benefits listed</p>}
                                            </div>
                                            <div className="plan-card__footer"><span><Users size={14} /> {subscriptionCount} {isFreePlan ? (typeFilter === 'member' ? 'member' : 'provider') : 'subscriber'}{subscriptionCount === 1 ? '' : 's'}</span><button className={plan.isActive ? 'is-hide' : 'is-show'} onClick={() => handleToggleActive(plan)}>{plan.isActive ? <EyeOff size={14} /> : <Eye size={14} />}{plan.isActive ? 'Hide plan' : 'Publish plan'}</button></div>
                                        </article>
                                    );
                                })}
                            </div>
                        )}
                    </section>

                    <section className="plans-assign">
                        <div className="plans-assign__intro"><span><Gift size={20} /></span><div><h2>Activate a plan</h2><p>Collect a Razorpay payment for a {typeFilter === 'member' ? 'member' : 'service provider'}, or gift a free subscription without any payment.</p></div></div>
                        {audienceUsers.length === 0 ? (
                            <div className="plans-assign__warning"><AlertTriangle size={15} /> No {typeFilter === 'member' ? 'members' : 'service providers'} are available yet.</div>
                        ) : (
                            <form onSubmit={handleAssign}>
                                <div className="plans-assign__field"><span>{typeFilter === 'member' ? 'Member' : 'Service provider'}</span><Select value={assignUserId} onChange={value => { setAssignUserId(value); setAssignPlanId(''); }} options={audienceUsers.map(user => ({ value: user._id, label: `${typeFilter === 'service' ? (user.businessName || user.name) : user.name} — ${user.mobile}` }))} placeholder={`Choose ${typeFilter === 'member' ? 'a member' : 'a provider'}`} ariaLabel="Select account" /></div>
                                <div className="plans-assign__field"><span>Plan</span><Select value={assignPlanId} onChange={setAssignPlanId} options={assignablePlans.map(plan => ({ value: plan._id, label: `${plan.name} — ₹${plan.price.toLocaleString('en-IN')}` }))} placeholder="Choose a published plan" disabled={!assignUserId} ariaLabel="Select plan" /></div>
                                <div className="plans-assign__mode" role="group" aria-label="Subscription type">
                                    <button type="button" className={assignMode === 'gift' ? 'is-active' : ''} onClick={() => setAssignMode('gift')}><Gift size={14} /> Gift (free)</button>
                                    <button type="button" className={assignMode === 'paid' ? 'is-active' : ''} onClick={() => setAssignMode('paid')}><IndianRupee size={14} /> Paid</button>
                                </div>
                                {assignMode === 'paid' && (
                                    <div className="plans-assign__pay">
                                        <ShieldCheck size={15} />
                                        <span>{selectedAssignPlan ? <>Razorpay Checkout will collect <b>₹{pricingForPlan(selectedAssignPlan).total.toLocaleString('en-IN')}</b>, including {pricingForPlan(selectedAssignPlan).gstPercentage}% GST (₹{pricingForPlan(selectedAssignPlan).gstAmount.toLocaleString('en-IN')}). The plan activates once verified.</> : 'Choose a plan — Razorpay Checkout opens to collect the payment.'}</span>
                                    </div>
                                )}
                                <label className="plans-assign__field plans-assign__note"><span>Note (optional)</span><input value={assignNote} maxLength={300} placeholder={assignMode === 'gift' ? 'Why is this gifted? e.g. festival offer' : 'Internal note'} onChange={event => setAssignNote(event.target.value)} /></label>
                                <button type="submit" disabled={assigning || !assignUserId || !assignPlanId}>{assignMode === 'gift' ? <Gift size={15} /> : <IndianRupee size={15} />}{assigning ? (assignMode === 'paid' ? 'Waiting for payment…' : 'Saving…') : assignMode === 'gift' ? 'Gift plan' : `Pay${selectedAssignPlan ? ' ' + pricingForPlan(selectedAssignPlan).total.toLocaleString('en-IN') : ''} with Razorpay`}</button>
                            </form>
                        )}
                    </section>

                    {renderHistory(historyTab)}
                </div>
            </main>

            <ConfirmDialog open={Boolean(pendingDelete)} title="Delete Plan?" message={pendingDelete ? `Delete “${pendingDelete.name}”?` : ''} items={['Existing subscribers keep their current entitlement', 'The plan is removed from all future assignments']} confirmLabel="Delete Plan" danger onConfirm={confirmDeletePlan} onCancel={() => setPendingDelete(null)} />

            {detailSub && (
                <div className="plans-detail-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) setDetailSub(null); }}>
                    <section className="plans-detail" role="dialog" aria-modal="true" aria-labelledby="plans-detail-title">
                        <header className="plans-detail__header">
                            <div className="plans-detail__heading">
                                <span className="plans-detail__icon"><CreditCard size={21} /></span>
                                <div>
                                    <span className="plans-detail__eyebrow">Subscription details</span>
                                    <h3 id="plans-detail-title">{detailSub.manual ? 'Manually enabled Premium' : `${detailSub.planNameSnapshot || 'Plan'} plan`}</h3>
                                    <p>Complete plan, payment and subscriber information</p>
                                </div>
                            </div>
                            <button className="plans-detail__close" type="button" onClick={() => setDetailSub(null)} aria-label="Close plan details"><X size={19} /></button>
                        </header>

                        <div className="plans-detail__body">
                            <section className="plans-detail__subscriber" aria-label="Subscriber">
                                <div className="plans-detail__avatar"><UserRound size={21} /></div>
                                <div className="plans-detail__subscriber-copy">
                                    <span>Subscriber</span>
                                    <strong>{detailUser.name || detailUser.businessName || detailCustomer.name || 'Account'}</strong>
                                    <p>{detailUser.role === 'service' || detailSub.targetType === 'service' ? 'Service provider account' : 'Matrimony member account'}</p>
                                </div>
                                <div className="plans-detail__contact">
                                    <span><Phone size={13} /> {detailUser.mobile || detailCustomer.mobile || 'Mobile not available'}</span>
                                    <span>{detailUser.email || detailCustomer.email || 'Email not available'}</span>
                                </div>
                                {!detailSub.manual && (
                                    <span className={`plans-detail__status is-${detailSub.cancelledAt ? 'cancelled' : detailIsLive(detailSub) ? 'active' : 'expired'}`}>
                                        <i /> {detailStatus(detailSub)}
                                    </span>
                                )}
                            </section>

                            {detailSub.manual ? (
                                <div className="plans-detail__manual">
                                    <ShieldCheck size={20} />
                                    <div><strong>No subscription record is attached</strong><p>This account was switched to Premium manually. Pricing, validity, payment and invoice details were not recorded.</p></div>
                                </div>
                            ) : (
                                <>
                                    <div className="plans-detail__summary">
                                        <article><span>Amount paid</span><strong>{detailSub.paymentStatus === 'gifted' ? 'Free' : detailMoney(detailTotal, detailSub.currency)}</strong><small>{titleCase(detailSub.paymentStatus || 'recorded')}</small></article>
                                        <article><span>Validity</span><strong>{planValidity(detailSub)}</strong><small>{detailSub.noExpiry ? 'No expiration date' : `${detailDate(detailSub.purchasedAt)} – ${detailDate(detailSub.expiresAt)}`}</small></article>
                                        <article><span>Access</span><strong>{detailStatus(detailSub)}</strong><small>{detailSub.cancelledAt ? `Ended ${detailDate(detailSub.cancelledAt)}` : detailSub.noExpiry ? 'Lifetime access' : `Until ${detailDate(detailSub.expiresAt)}`}</small></article>
                                    </div>

                                    <div className="plans-detail__columns">
                                        <section className="plans-detail__panel">
                                            <div className="plans-detail__panel-title"><CalendarDays size={16} /><div><h4>Plan & access</h4><p>Entitlement and assignment</p></div></div>
                                            <dl className="plans-detail__list">
                                                <div><dt>Plan</dt><dd>{detailSub.planNameSnapshot || '—'}</dd></div>
                                                <div><dt>Audience</dt><dd>{detailSub.targetType === 'service' ? 'Service provider' : 'Member'}</dd></div>
                                                <div><dt>Started on</dt><dd>{detailDate(detailSub.purchasedAt)}</dd></div>
                                                <div><dt>Expires on</dt><dd>{detailSub.noExpiry ? 'Never (lifetime)' : detailDate(detailSub.expiresAt)}</dd></div>
                                                <div><dt>Assigned through</dt><dd>{detailSource}</dd></div>
                                                <div><dt>Subscription ID</dt><dd className="is-code">{detailSub._id || '—'}</dd></div>
                                            </dl>
                                        </section>

                                        <section className="plans-detail__panel">
                                            <div className="plans-detail__panel-title"><IndianRupee size={16} /><div><h4>Payment breakdown</h4><p>Frozen at the time of purchase</p></div></div>
                                            <dl className="plans-detail__list">
                                                <div><dt>Subtotal</dt><dd>{detailMoney(detailSubtotal, detailSub.currency)}</dd></div>
                                                <div><dt>GST ({Number(detailSub.gstPercentageSnapshot || 0)}%)</dt><dd>{detailMoney(detailTax, detailSub.currency)}</dd></div>
                                                <div className="is-total"><dt>Total paid</dt><dd>{detailSub.paymentStatus === 'gifted' ? 'Free' : detailMoney(detailTotal, detailSub.currency)}</dd></div>
                                                <div><dt>Method</dt><dd>{detailSub.paymentStatus === 'gifted' ? 'Admin gift' : titleCase(detailSub.paymentMethod || detailSub.paymentProvider || 'Not recorded')}</dd></div>
                                                <div><dt>Payment status</dt><dd>{titleCase(detailSub.paymentStatus || 'Recorded')}</dd></div>
                                                <div><dt>Currency</dt><dd>{detailSub.currency || 'INR'}</dd></div>
                                            </dl>
                                        </section>
                                    </div>

                                    <section className="plans-detail__panel plans-detail__billing">
                                        <div className="plans-detail__panel-title"><FileText size={16} /><div><h4>Invoice & references</h4><p>Billing identifiers and gateway records</p></div></div>
                                        <dl className="plans-detail__reference-grid">
                                            <div><dt>Invoice number</dt><dd>{detailSub.invoiceNumber || 'Not generated'}</dd></div>
                                            <div><dt>Payment provider</dt><dd>{titleCase(detailSub.paymentProvider || detailSub.paymentMethod || 'Not recorded')}</dd></div>
                                            <div><dt>Order ID</dt><dd>{detailSub.paymentOrderId || '—'}</dd></div>
                                            <div><dt>Payment ID / reference</dt><dd>{detailSub.paymentId || detailSub.paymentReference || '—'}</dd></div>
                                        </dl>
                                        {(detailCustomer.name || detailCustomer.email || detailCustomer.mobile || detailAddress) && (
                                            <div className="plans-detail__bill-to">
                                                <span>Bill to</span>
                                                <strong>{detailCustomer.name || detailUser.name || detailUser.businessName || 'Subscriber'}</strong>
                                                <p>{[detailCustomer.email, detailCustomer.mobile].filter(Boolean).join(' · ')}</p>
                                                {detailAddress && <p>{detailAddress}</p>}
                                            </div>
                                        )}
                                    </section>

                                    {!!detailSub.featuresSnapshot?.length && (
                                        <section className="plans-detail__panel plans-detail__benefits">
                                            <div className="plans-detail__panel-title"><Sparkles size={16} /><div><h4>Benefits included</h4><p>{detailSub.featuresSnapshot.length} plan benefit{detailSub.featuresSnapshot.length === 1 ? '' : 's'}</p></div></div>
                                            <div className="plans-detail__feature-list">
                                                {detailSub.featuresSnapshot.map(feature => <span key={feature}><Check size={13} /> {feature}</span>)}
                                            </div>
                                        </section>
                                    )}

                                    {(detailSub.adminNote || detailSub.cancelledAt) && (
                                        <section className={`plans-detail__notice ${detailSub.cancelledAt ? 'is-cancelled' : ''}`}>
                                            <Clock3 size={17} />
                                            <div>
                                                <strong>{detailSub.cancelledAt ? 'Subscription ended' : 'Admin note'}</strong>
                                                {detailSub.cancelledAt && <p>Cancelled by {titleCase(detailSub.cancelledBy || 'admin')} on {detailDate(detailSub.cancelledAt)}{detailSub.cancelReason ? ` · ${detailSub.cancelReason}` : ''}</p>}
                                                {detailSub.adminNote && <p>{detailSub.adminNote}</p>}
                                            </div>
                                        </section>
                                    )}
                                </>
                            )}
                        </div>

                        <footer className="plans-detail__footer">
                            <div>
                                {!detailSub.manual && <button type="button" onClick={() => setInvoiceSubscription(detailSub)}><FileText size={14} /> View invoice</button>}
                                {!detailSub.manual && <button type="button" onClick={() => downloadSubscriptionInvoicePdf(detailSub, detailUser)}><Download size={14} /> Download PDF</button>}
                            </div>
                            <div>
                                <button type="button" onClick={() => setDetailSub(null)}>Close</button>
                                {(detailSub.manual || detailIsLive(detailSub)) && (
                                    <button type="button" className="is-danger" disabled={moving} onClick={handleMoveToFree}>{moving ? 'Moving…' : 'Move to free plan'}</button>
                                )}
                            </div>
                        </footer>
                    </section>
                </div>
            )}

            {invoiceSubscription && (
                <SubscriptionInvoice subscription={invoiceSubscription} user={invoiceSubscription.user || {}} onClose={() => setInvoiceSubscription(null)} />
            )}
        </div>
    );
};

export default Plans;
